import React, { useEffect, useState } from "react";
import { Box, Button, Typography, keyframes } from "@mui/material";
import { HOL, holFadeUp, holShimmer } from "../UserSide/pages/holTheme";
import HolLeaderboard from "./HolLeaderboard";

const LEADERBOARD_DELAY_MS = 1800;

const slamIn = keyframes`
  0% { opacity: 0; transform: scale(2.4); letter-spacing: 0.4em; filter: blur(8px); }
  55% { opacity: 1; transform: scale(0.94); letter-spacing: 0.04em; filter: blur(0); }
  75% { transform: scale(1.04); }
  100% { opacity: 1; transform: scale(1); letter-spacing: 0.06em; }
`;

const settleUp = keyframes`
  from { transform: translateY(0) scale(1); }
  to { transform: translateY(0) scale(0.62); }
`;

/**
 * Broadcast end-of-session screen: "GAME OVER" slam, then the final leaderboard.
 * `results` is the server's final_results payload from `holsp:session_ended`.
 */
const HolGameOverScreen = ({ results, viewerDeviceId = null, onClose = null, large = false }) => {
  const [showBoard, setShowBoard] = useState(false);
  const leaderboard = results?.leaderboard || [];
  const viewerEntry = viewerDeviceId
    ? leaderboard.find((e) => e.device_id && String(e.device_id) === String(viewerDeviceId))
    : null;

  useEffect(() => {
    setShowBoard(false);
    const t = setTimeout(() => setShowBoard(true), LEADERBOARD_DELAY_MS);
    return () => clearTimeout(t);
  }, [results?.session_id]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: showBoard ? "flex-start" : "center",
        textAlign: "center",
        px: 2,
        py: { xs: 2, sm: 3 },
        overflowY: "auto",
      }}
    >
      <Typography
        sx={{
          fontFamily: HOL.fontDisplay,
          fontWeight: 900,
          lineHeight: 1,
          fontSize: large ? { xs: "4rem", md: "7rem" } : { xs: "3.2rem", sm: "4.4rem" },
          background: `linear-gradient(90deg, ${HOL.higher}, ${HOL.gold}, ${HOL.lower}, ${HOL.gold}, ${HOL.higher})`,
          backgroundSize: "200% auto",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          filter: "drop-shadow(0 0 30px rgba(245,215,110,0.35))",
          animation: showBoard
            ? `${holShimmer} 4s linear infinite, ${settleUp} 0.5s ease forwards`
            : `${slamIn} 0.9s cubic-bezier(0.22,1,0.36,1) both, ${holShimmer} 4s linear infinite`,
          transformOrigin: "center top",
          mb: showBoard ? -1 : 1.5,
        }}
      >
        GAME OVER
      </Typography>

      {!showBoard ? (
        <Typography
          sx={{
            color: HOL.muted,
            fontWeight: 600,
            fontSize: { xs: "0.95rem", sm: "1.05rem" },
            animation: `${holFadeUp} 0.5s ease both`,
            animationDelay: "0.7s",
          }}
        >
          The host has ended the game. Final results coming up…
        </Typography>
      ) : (
        <Box sx={{ width: "100%", maxWidth: large ? 760 : 520, animation: `${holFadeUp} 0.5s ease both` }}>
          <Typography
            sx={{
              fontFamily: HOL.fontDisplay,
              fontWeight: 800,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: HOL.gold,
              fontSize: large ? "1.05rem" : "0.8rem",
              mb: 0.5,
            }}
          >
            Final Results
          </Typography>
          {viewerEntry ? (
            <Typography sx={{ color: "#fff", fontWeight: 700, mb: 1.5 }}>
              You finished #{viewerEntry.rank} with {viewerEntry.score} / {viewerEntry.total_rounds}
            </Typography>
          ) : (
            <Typography sx={{ color: HOL.muted, mb: 1.5, fontSize: "0.9rem" }}>
              {leaderboard.length
                ? `${leaderboard.length} player${leaderboard.length === 1 ? "" : "s"} took the hot seat`
                : "Thanks for playing!"}
            </Typography>
          )}
          <Box
            sx={{
              p: { xs: 1.25, sm: 2 },
              borderRadius: "20px",
              background: HOL.panel,
              border: `1px solid ${HOL.panelBorder}`,
              backdropFilter: "blur(16px)",
            }}
          >
            <HolLeaderboard entries={leaderboard} highlightDeviceId={viewerDeviceId} maxRows={large ? 10 : 12} />
          </Box>
          {onClose ? (
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                mt: 2.5,
                px: 4,
                py: 1,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                color: "#fff",
                borderColor: "rgba(255,255,255,0.28)",
                "&:hover": { borderColor: "rgba(255,255,255,0.5)", bgcolor: "rgba(255,255,255,0.06)" },
              }}
            >
              Done
            </Button>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default HolGameOverScreen;
